// dependencies import
import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
// assets and scss import
import "./dayCollectionView.scss";
import tickIcon from "../../../assets/tick-thin.svg";
import tickBlueIcon from "../../../assets/tick-thin-blue.svg";

// project file import
import CollectionSummaryTableView from "./CollectionSummaryTableView";
import CollectionDetailsTableView from "./CollectionDetailsTableView";
import { Drawer } from "@mui/material";
import closeIcon from "../../../assets/crossIcon.svg";
import SearchBarWithIcon from "../../common/Modules/SearchBarWithIcon";
import PrimaryButton from "../../common/Buttons/PrimaryButton";
import SecondaryButton from "../../common/Buttons/SecondaryButton";
import { c2cPostApiHandler } from "../../../Util/c2cApiHandler";
import { LOCALSTORAGEKEY } from "../../../Util/Helper/localStorageUtils";
import { USER_ROLE, USER_TYPE } from "../../../Util/constants";
import {
  GET_DOCTOR_COLLECTIONS_DETAILS,
  GET_DOCTOR_COLLECTIONS_SUMMARY,
  GET_STAFF_COLLECTION_SUMMARY,
  GET_STAFF_COLLECTIONS_DETAILS,
} from "../MyTransactionsApiHandler/ApiConstants";
import DayCollectionHeaderView from "./DayCollectionHeaderView";
import Loader from "../../UI Component/Loader/Loader";
import DownloadReport from "../../DownloadReport/DownloadReport";

const DayCollectionView = ({
  getDoctorIds,
  displayTransactionDropdown,
  disableDayCollectionDownload,
  downloadDayCollectionReportAPICall,
}) => {
  const [showFlag, setShowFlag] = useState(true);
  const [date, setDate] = useState(moment().format("MMM DD, YYYY"));
  const [filterDrawerFlag, setFilterDrawerFlag] = useState(false);
  const [searchStr, setSearchStr] = useState("");
  const [collectionSummary, setCollectionSummary] = useState([]);
  const [collectionDetails, setCollectionDetails] = useState([]);
  const [totalSummaryAmount, setTotalSummaryAmount] = useState({
    sum_of_cash: "",
    sum_of_online_amount: "",
    sum_of_other_amount: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState("");
  const [showLoader, setShowLoader] = useState(true);
  const [userListObj, setUserListObj] = useState({
    originalList: [{ user_id: 1, user_name: "All", selected: true }],
    tempList: [],
    filterApplied: false,
  });

  const clinicId = localStorage.getItem(LOCALSTORAGEKEY.CLINIC_ID);
  const doctorRole = localStorage.getItem(LOCALSTORAGEKEY.DOCTOR_ROLE);
  const selectedClinic = localStorage.getItem(
    LOCALSTORAGEKEY.SELECTED_CLINIC_DETAILS
  );
  const userType = localStorage.getItem(LOCALSTORAGEKEY.USER_TYPE);
  const staffId = localStorage.getItem(LOCALSTORAGEKEY.STAFF_ID);
  const prevClinicRef = useRef(null);

  // lifecycle
  useEffect(() => {
    getCollectionSummaryAPICall();
    getCollectionDetailsAPICall();
  }, [clinicId]);

  // Api Calls
  const getCollectionSummaryAPICall = (requestPayload) => {
    let ids = getDoctorIds();
    let doctorIds = ids?.doctor_id ? [ids?.doctor_id] : [...ids?.doctor_ids];
    let payload = {
      clinic_id: clinicId,
      doctor_ids: doctorIds,
      user_ids: [],
      date: moment(date).format("YYYY-MM-DD"),
      is_admin:
        doctorRole === USER_ROLE.DOCTOR_ADMIN ||
        doctorRole === USER_ROLE.RESOURCE_ADMIN ||
        (userType === USER_TYPE.STAFF && checkPermission()),
      ...(userType === USER_TYPE.STAFF && { staff_id: staffId }),
      ...requestPayload,
    };
    let api = !checkPermission()
      ? GET_STAFF_COLLECTION_SUMMARY
      : GET_DOCTOR_COLLECTIONS_SUMMARY;
    const resData = c2cPostApiHandler(api, payload);
    resData.then(([res, error]) => {
      if (!error) {
        if (res?.data?.statusCode === 200) {
          let tempCollectionSummary =
            res?.data?.data?.length > 0 ? res?.data?.data : [];

          tempCollectionSummary.forEach((data) => {
            data["total_calculated_amount"] =
              (data?.total_cash_amount ? data?.total_cash_amount : 0) +
              (data?.total_online_amount ? data?.total_online_amount : 0) +
              (data?.total_other_amount ? data?.total_other_amount : 0);
          });

          setCollectionSummary(tempCollectionSummary);
          setTotalSummaryAmount({
            sum_of_cash: tempCollectionSummary.reduce(
              (a, v) => a + (v?.total_cash_amount ?? 0),
              0
            ),
            sum_of_online_amount: tempCollectionSummary.reduce(
              (a, v) => a + (v?.total_online_amount ?? 0),
              0
            ),
            sum_of_other_amount: tempCollectionSummary.reduce(
              (a, v) => a + (v?.total_other_amount ?? 0),
              0
            ),
            sum_of_total_calculated_amount: tempCollectionSummary.reduce(
              (a, v) => a + (v?.total_calculated_amount ?? 0),
              0
            ),
          });
        }
      } else {
      }
    });
  };
  const getCollectionDetailsAPICall = (requestPayload) => {
    setShowLoader(true);
    let ids = getDoctorIds();
    let doctorIds = ids?.doctor_id ? [ids?.doctor_id] : [...ids?.doctor_ids];
    let payload = {
      clinic_id: clinicId,
      doctor_ids: doctorIds,
      user_ids: [],
      page_no: currentPage,
      records_per_page: 10,
      date: moment(date).format("YYYY-MM-DD"),
      is_admin:
        doctorRole === USER_ROLE.DOCTOR_ADMIN ||
        doctorRole === USER_ROLE.RESOURCE_ADMIN ||
        (userType === USER_TYPE.STAFF && checkPermission()),
      ...(userType === USER_TYPE.STAFF && { staff_id: staffId }),
      ...requestPayload,
    };
    let api = !checkPermission()
      ? GET_STAFF_COLLECTIONS_DETAILS
      : GET_DOCTOR_COLLECTIONS_DETAILS;
    const resData = c2cPostApiHandler(api, payload);
    resData.then(([res, error]) => {
      if (!error) {
        if (res?.data?.statusCode === 200) {
          if (res?.data?.data?.length > 0) {
            setCollectionDetails(res?.data?.data);
            setTotalRecords(res?.data?.total_records);
            setShowLoader(false);
          } else {
            setCollectionDetails([]);
          }
          let checkClinicChanged = prevClinicRef.current === clinicId;
          prevClinicRef.current = clinicId;
          let selectedList = checkClinicChanged
            ? userListObj?.tempList
                .filter((data) => data?.selected && data?.user_id !== 1)
                .map((item) => item?.user_id)
            : [];
          let list = [
            {
              user_id: 1,
              user_name: "All",
              selected: selectedList?.length === 0,
            },
          ];
          if (res?.data?.users_list?.length > 0) {
            res.data.users_list.forEach((user) => {
              user["selected"] = selectedList.includes(user?.user_id);
              list.push(user);
            });
          }
          setUserListObj({
            tempList: [...list],
            originalList: [...list],
            filterApplied: selectedList?.length > 0,
          });
          setShowLoader(false);
        } else {
          setShowLoader(false);
        }
      } else {
        setShowLoader(false);
      }
    });
  };

  const handleDownloadDayCollection = () => {
    let ids = getDoctorIds();
    let tempUserList = [...userListObj?.tempList];
    const filteredUserList = tempUserList?.filter(
      (data) => data?.user_id !== 1 && data?.selected
    );
    let filteredUserIds = filteredUserList?.map((item) => item?.user_id);

    let doctorIds = ids?.doctor_id ? [ids?.doctor_id] : [...ids?.doctor_ids];
    let payload = {
      clinic_id: clinicId,
      doctor_ids: doctorIds,
      user_ids: [...filteredUserIds],
      date: date,
      is_admin:
        doctorRole === USER_ROLE.DOCTOR_ADMIN ||
        doctorRole === USER_ROLE.RESOURCE_ADMIN ||
        (userType === USER_TYPE.STAFF && checkPermission()),
      ...(userType === USER_TYPE.STAFF && { staff_id: staffId }),
      user_type: userType,
      is_staff_data: !checkPermission(),
      report_type: "daybook_collection",
    };
    downloadDayCollectionReportAPICall(payload);
  };

  // handlers
  const handleFilterSearch = (value) => {
    setSearchStr(value);
  };

  const handleApplyFilter = () => {
    setFilterDrawerFlag(false);
    setSearchStr("");
    let tempUserList = [...userListObj?.tempList];
    const filteredUserList = tempUserList?.filter(
      (data) => data?.user_id !== 1 && data?.selected
    );

    let filteredUserIds = filteredUserList?.map((item) => item?.user_id);
    setUserListObj({
      ...userListObj,
      originalList: [...userListObj?.tempList],
      filterApplied: filteredUserList?.length > 0,
    });
    setShowLoader(true);
    getCollectionDetailsAPICall({
      user_ids: [...filteredUserIds],
      page_no: 1,
    });
    getCollectionSummaryAPICall({ user_ids: [...filteredUserIds] });
    setCurrentPage(1);
  };

  const handleClearFilter = () => {
    setFilterDrawerFlag(false);
    setSearchStr("");
    let tempUserList = [...userListObj?.tempList];
    tempUserList.forEach((data) => {
      data["selected"] = data?.user_id === 1 ? true : false;
    });
    setUserListObj({
      ...userListObj,
      originalList: [...tempUserList],
      filterApplied: false,
    });
    getCollectionDetailsAPICall({
      user_ids: [],
      page_no: 1,
    });
    getCollectionSummaryAPICall({ user_ids: [] });
    setCurrentPage(1);
  };

  const handleSelectUser = (userId) => {
    let tempList = JSON.parse(JSON.stringify(userListObj?.tempList));
    let index = tempList?.findIndex((data) => data?.user_id === userId);
    if (index > -1) {
      tempList[index].selected =
        userId === 1 ? true : !tempList[index].selected;
      if (userId !== 1) {
        let filteredList = tempList.filter(
          (data) => data?.selected && data?.user_id !== 1
        );
        let defaultUserIndex = tempList?.findIndex(
          (data) => data?.user_id === 1
        );
        tempList[defaultUserIndex].selected = filteredList?.length === 0;
      } else {
        tempList.forEach((data) => (data.selected = data?.user_id === 1));
      }
      setUserListObj({
        ...userListObj,
        tempList: tempList,
      });
    }
  };

  const onPageChange = (pageNo) => {
    let tempUserList = [...userListObj?.tempList];
    const filteredUserList = tempUserList?.filter(
      (data) => data?.user_id !== 1 && data?.selected
    );
    let filteredUserIds = filteredUserList?.map((item) => item?.user_id);

    setCurrentPage(pageNo);
    let params = {
      page_no: pageNo,
      user_ids: [...filteredUserIds],
    };

    getCollectionDetailsAPICall(params);
  };

  const handleCloseFilter = () => {
    setUserListObj({
      ...userListObj,
      tempList: [...userListObj?.originalList],
    });
    setFilterDrawerFlag(false);
  };

  const handleOpenFilter = () => {
    setFilterDrawerFlag(true);
  };

  const handleDate = (date) => {
    setDate(date);
    let tempUserList = [...userListObj?.tempList];
    tempUserList.forEach((data) => {
      data["selected"] = data?.user_id === 1;
    });
    setUserListObj({
      ...userListObj,
      originalList: [...tempUserList],
      filterApplied: false,
    });

    getCollectionDetailsAPICall({
      user_ids: [],
      page_no: 1,
      date: date,
    });
    getCollectionSummaryAPICall({ user_ids: [], date: date });
    setCurrentPage(1);
  };

  const checkPermission = () => {
    let selectedClinicDetails = selectedClinic
      ? JSON.parse(selectedClinic)
      : {};

    if (
      Object.keys(selectedClinicDetails)?.length > 0 &&
      Object.keys(selectedClinicDetails)?.includes("permission_set") &&
      userType === USER_TYPE.STAFF &&
      clinicId
    ) {
      let checkPermission = true;
      if (
        Object.keys(selectedClinicDetails?.permission_set)?.length > 0 &&
        Object.keys(selectedClinicDetails?.permission_set)?.includes(
          "clinictransactionreport"
        )
      ) {
        checkPermission =
          selectedClinicDetails?.permission_set?.clinictransactionreport[0] ===
            1 &&
          selectedClinicDetails?.permission_set?.clinictransactionreport[1] ===
            1;
      }
      return checkPermission;
    } else {
      return true;
    }
  };

  // display UI
  const displayFilterUI = () => {
    let filteredList = userListObj.tempList?.filter((data) =>
      data?.user_name?.toLowerCase()?.includes(searchStr?.toLowerCase())
    );
    return (
      <Drawer anchor={"right"} open={filterDrawerFlag}>
        <div className="dcv-drawer">
          <div className="dcv-drawer-header">
            <h2>Select Filter</h2>
            <button onClick={handleCloseFilter}>
              <img src={closeIcon} alt="" />
            </button>
          </div>
          <div className="dcv-drawer-body">
            <h3>Please Select User Name</h3>
            <div className="dcv-drawer-body-search-input">
              <SearchBarWithIcon
                placeholder="Search"
                inputCallBack={handleFilterSearch}
                search_string={searchStr}
                isClearable={true}
              />
            </div>
            <ul className="dcv-filter-list">
              {filteredList.map((user, index) => (
                <li key={index} onClick={() => handleSelectUser(user?.user_id)}>
                  <img src={user?.selected ? tickBlueIcon : tickIcon} alt="" />
                  <span>{user?.user_name}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="dcv-drawer-footer">
            <PrimaryButton
              title={"Apply"}
              handleButtonClick={handleApplyFilter}
            />
            <SecondaryButton
              title={"Clear"}
              handleButtonClick={handleClearFilter}
            />
          </div>
        </div>
      </Drawer>
    );
  };

  const displayDayCollectionUI = () => {
    return (
      <div className="day-collection-view">
        <DayCollectionHeaderView
          showFlag={showFlag}
          setShowFlag={setShowFlag}
          date={date}
          totalSummaryAmount={totalSummaryAmount}
          handleDate={handleDate}
          handleOpenFilter={handleOpenFilter}
          displayTransactionDropdown={displayTransactionDropdown}
          filterApplied={userListObj?.filterApplied}
          disableDayCollectionDownload={disableDayCollectionDownload}
          handleDownloadDayCollection={handleDownloadDayCollection}
        />
        <CollectionSummaryTableView
          showFlag={showFlag}
          collectionSummary={collectionSummary}
          totalSummaryAmount={totalSummaryAmount}
        />
        <CollectionDetailsTableView
          showFlag={showFlag}
          totalRecords={totalRecords}
          setShowFlag={setShowFlag}
          collectionDetails={collectionDetails}
          headingFlag={window.innerWidth > 768 ? showFlag : true}
          currentPage={currentPage}
          onPageChange={onPageChange}
        />
        {displayFilterUI()}
      </div>
    );
  };

  return (
    <div>
      {showLoader && <Loader />}
      {displayDayCollectionUI()}
    </div>
  );
};

export default DayCollectionView;
